import { useState } from 'react';

import { ClientForm } from '@/shared/clients/ClientForm';
import { CLIENT_ORIGIN_LABELS, tradeNameOf, type Client } from '@/shared/clients/types';
import { useClientList, useCreateClient, useUpdateClient } from '@/shared/clients/useClients';
import { isManager, useFeatures, useSessionUser } from '@/shared/session/currentUser';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Card } from '@/shared/ui/Card';
import { Dialog } from '@/shared/ui/Dialog';
import { CardList, CardListItem } from '@/shared/ui/CardList';
import { EmptyState } from '@/shared/ui/EmptyState';
import { ErrorMessage } from '@/shared/ui/ErrorMessage';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Spinner } from '@/shared/ui/Spinner';
import { TBody, THead, Table, Td, Th, Tr } from '@/shared/ui/Table';
import { PlusIcon, SearchIcon, UsersIcon } from '@/shared/ui/icons';
import { useDebounced } from '@/shared/utils/useDebounced';

import { ProferSync } from './ProferSync';

/**
 * The customer master, as far as this app is concerned.
 *
 * It is a **local cache**, not the master: rows get here by hand, through an import, or
 * from Profer's list once a night (contract v10.46, `features.erp_clients`), and `source`
 * says which. Saying so on screen
 * matters, because a row that came from the ERP will be overwritten by the next import
 * and one typed here will not. It is said **by exception**: the origin column only
 * appears when some row on the page did not come from here, and the state column only
 * when some row is inactive, so the everyday list is four columns and nothing else.
 */
const PAGE_SIZE = 50;

const PROFER_HELP = [
  <>
    La lista de <strong>Profer</strong> se trae sola cada noche. Quien administra puede pedirla
    antes con «Actualizar desde Profer».
  </>,
  <>
    Un dato mal en Profer se corrige en Profer: si se cambia aquí, la noche siguiente lo vuelve a
    traer como venga. El informe de cada actualización dice cuáles llegan mal.
  </>,
];

const HELP = [
  <>Pulsa una fila para abrir la ficha y editarla.</>,
  <>
    Se dan de alta aquí o desde el presupuesto («Cliente nuevo» en el selector). Es la misma lista.
  </>,
  <>
    El <strong>código de cliente</strong> es el del programa de gestión. Ponlo si lo tienes: evita
    duplicados el día que se cargue la lista entera.
  </>,
  <>
    Una ficha que vino del programa de gestión lleva su origen al lado, y la próxima carga la
    sobreescribe. Las escritas aquí no.
  </>,
  <>
    Un cliente no se borra: se da de baja, y deja de salir salvo que marques «Incluir los dados de
    baja».
  </>,
];

/** Postal code and city on one line: the part of the address a list needs. */
function locality(client: Client): string {
  return [client.postal_code, client.city].filter(Boolean).join(' ');
}

function originLabel(client: Client): string | null {
  const source = client.source ?? 'manual';
  if (source === 'manual') return null;
  return CLIENT_ORIGIN_LABELS[source] ?? source;
}

export function ClientsPage() {
  const { erp_clients: proferOn = false } = useFeatures();
  const manager = isManager(useSessionUser());
  const [search, setSearch] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Client | null>(null);

  const [page, setPage] = useState(0);
  const clients = useClientList({
    search: useDebounced(search).trim(),
    include_inactive: includeInactive,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  });
  const create = useCreateClient();

  // One form at a time. The sheet is modal, so a second click cannot land on a row while
  // one is half filled in; `busy` keeps the rows quiet under it all the same.
  const busy = adding || editing !== null;
  const items = clients.data?.items ?? [];
  const showOrigin = items.some((client) => originLabel(client) !== null);
  const showState = items.some((client) => client.is_active === false);

  const newButton = (
    <Button icon={<PlusIcon size={18} />} onClick={() => setAdding(true)}>
      Nuevo cliente
    </Button>
  );

  return (
    <>
      <PageHeader
        title="Clientes"
        help={proferOn ? [...HELP, ...PROFER_HELP] : HELP}
        actions={newButton}
      />

      {proferOn && manager ? <ProferSync /> : null}

      {adding ? (
        <Dialog
          title="Nuevo cliente"
          description="Solo la razón social es obligatoria; el resto se completa cuando se sepa."
          size="lg"
          onClose={() => setAdding(false)}
        >
          <ClientForm
            saving={create.isPending}
            error={create.isError ? create.error : null}
            submitLabel="Crear cliente"
            onCancel={() => setAdding(false)}
            onSubmit={(payload) => create.mutate(payload, { onSuccess: () => setAdding(false) })}
          />
        </Dialog>
      ) : null}

      {editing ? (
        // The `key` is the fix for a real bug and not a lint tidy up: without it, editing
        // one client and then another kept the first one's values on screen while the
        // PATCH went to the second one's URL, overwriting its name, NIF and address.
        <EditClient key={editing.id} client={editing} onDone={() => setEditing(null)} />
      ) : null}

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="relative w-full max-w-sm">
          <SearchIcon
            size={16}
            className="text-ink-4 pointer-events-none absolute top-1/2 left-3 -translate-y-1/2"
          />
          <input
            type="search"
            aria-label="Buscar"
            placeholder="Nombre, código de cliente o NIF"
            className="field w-full pl-9"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(0);
            }}
          />
        </div>
        <label className="text-ink-2 flex items-center gap-2 text-[14px]">
          <input
            type="checkbox"
            className="accent-accent size-4"
            checked={includeInactive}
            onChange={(event) => setIncludeInactive(event.target.checked)}
          />
          Incluir los dados de baja
        </label>
      </div>

      {clients.isPending ? <Spinner variant="block" label="Cargando clientes" /> : null}
      {clients.isError ? (
        <ErrorMessage error={clients.error}>No se pudo cargar la lista de clientes</ErrorMessage>
      ) : null}

      {clients.data && items.length === 0 ? (
        <EmptyState
          icon={<UsersIcon size={24} />}
          title="No hay clientes"
          hint={
            search.trim()
              ? 'Ninguno con ese nombre, código ni NIF.'
              : 'Se dan de alta aquí, desde el editor de presupuestos, o cargando la lista del programa de gestión.'
          }
          action={!search.trim() ? newButton : undefined}
        />
      ) : null}

      {clients.data && items.length > 0 ? (
        <>
          <div className="hidden md:block">
            <Card padded={false}>
              <Table label="Clientes" minWidth="0">
                <THead>
                  <tr>
                    <Th>Nombre</Th>
                    <Th className="w-32">Código</Th>
                    <Th className="w-32">NIF</Th>
                    <Th>Localidad</Th>
                    {showOrigin ? <Th className="w-40">Origen</Th> : null}
                    {showState ? <Th className="w-24">Estado</Th> : null}
                  </tr>
                </THead>
                <TBody>
                  {items.map((client) => {
                    const origin = originLabel(client);
                    return (
                      <Tr
                        key={client.id}
                        interactive={!busy}
                        onClick={busy ? undefined : () => setEditing(client)}
                      >
                        <Td className="text-ink font-medium">
                          {client.name}
                          {tradeNameOf(client) ? (
                            <span className="text-ink-3 block text-[13px] font-normal">
                              {tradeNameOf(client)}
                            </span>
                          ) : null}
                        </Td>
                        <Td className="text-ink-2 tabular-nums">{client.code ?? ''}</Td>
                        <Td className="text-ink-2">{client.nif ?? ''}</Td>
                        <Td className="text-ink-2">
                          {locality(client)}
                          {client.province ? (
                            <span className="text-ink-3 ml-2 text-[13px]">{client.province}</span>
                          ) : null}
                        </Td>
                        {showOrigin ? (
                          <Td className="text-ink-3 text-[13px]">{origin ?? ''}</Td>
                        ) : null}
                        {showState ? (
                          <Td>
                            {client.is_active === false ? (
                              <Badge tone="neutral">De baja</Badge>
                            ) : null}
                          </Td>
                        ) : null}
                      </Tr>
                    );
                  })}
                </TBody>
              </Table>
            </Card>
          </div>

          <CardList label="Clientes" className="md:hidden">
            {items.map((client) => {
              const origin = originLabel(client);
              const inactive = client.is_active === false;
              const where = [locality(client), client.province].filter(Boolean).join(' · ');
              return (
                <CardListItem
                  key={client.id}
                  title={client.name}
                  subtitle={
                    [tradeNameOf(client), client.code, client.nif].filter(Boolean).join(' · ') ||
                    undefined
                  }
                  aside={
                    origin || inactive ? (
                      <>
                        {origin ? <Badge tone="info">{origin}</Badge> : null}
                        {inactive ? <Badge tone="neutral">De baja</Badge> : null}
                      </>
                    ) : undefined
                  }
                  footer={where ? <span>{where}</span> : undefined}
                  {...(busy ? {} : { onClick: () => setEditing(client) })}
                />
              );
            })}
          </CardList>
        </>
      ) : null}

      {clients.data && clients.data.total > PAGE_SIZE ? (
        <div className="text-ink-3 flex items-center justify-end gap-3 text-[13px]">
          <span>
            {page * PAGE_SIZE + 1}–{page * PAGE_SIZE + items.length} de {clients.data.total}
          </span>
          <Button
            variant="secondary"
            size="sm"
            disabled={page === 0}
            onClick={() => setPage((current) => Math.max(0, current - 1))}
          >
            Anteriores
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={(page + 1) * PAGE_SIZE >= clients.data.total}
            onClick={() => setPage((current) => current + 1)}
          >
            Siguientes
          </Button>
        </div>
      ) : null}
    </>
  );
}

/**
 * The file of a client, as a sheet over the list: the row that opened it may be fifty
 * rows down, and a form that opens where the eyes are is one that does not need to be
 * scrolled to.
 */
function EditClient({ client, onDone }: { client: Client; onDone: () => void }) {
  const update = useUpdateClient(client.id);

  return (
    <Dialog
      title={`Editar ${client.name}`}
      size="lg"
      onClose={onDone}
      description={
        // The ERP is the master for a row it maintains, so an edit here is temporary for
        // those. Better said than discovered after the next import.
        client.source === 'erp_api'
          ? 'Esta ficha viene de Profer: la actualización de cada noche sobreescribirá lo que cambies aquí. Lo que esté mal, corrígelo en Profer.'
          : client.source !== 'manual'
            ? 'Esta ficha la mantiene el programa de gestión: la próxima importación sobreescribirá lo que cambies aquí.'
            : undefined
      }
    >
      <ClientForm
        key={client.id}
        client={client}
        saving={update.isPending}
        error={update.isError ? update.error : null}
        submitLabel="Guardar cambios"
        onCancel={onDone}
        onSubmit={(payload) => update.mutate(payload, { onSuccess: onDone })}
      />
    </Dialog>
  );
}
